import { useState, useCallback, useEffect } from 'react';

export interface FileContentOrigin {
  filePath: string;
  sections: ContentSection[];
  lastModified: number;
}

export interface ContentSection {
  id: string;
  startLine: number;
  endLine: number;
  origin: 'ai' | 'user';
  content: string;
  timestamp: number;
}

export interface PendingEdit {
  id: string;
  filePath: string;
  content: string;
  timestamp: number;
  applied: boolean;
  conflicted: boolean;
}

export interface EditConflict {
  id: string;
  filePath: string;
  userContent: string;
  aiContent: string;
  timestamp: number;
  resolved: boolean;
  resolution?: 'user' | 'ai' | 'merge';
}

export interface ConcurrentEditingState {
  fileOrigins: Map<string, FileContentOrigin>;
  pendingEdits: PendingEdit[];
  conflicts: EditConflict[];
  isAIGenerating: boolean;
  currentAIFile?: string;
  userEditingFile?: string;
}

export function useConcurrentEditing() {
  const [state, setState] = useState<ConcurrentEditingState>({
    fileOrigins: new Map(),
    pendingEdits: [],
    conflicts: [],
    isAIGenerating: false,
    currentAIFile: undefined,
    userEditingFile: undefined
  });

  // Track AI generation status
  const updateAIStatus = useCallback((isGenerating: boolean, currentFile?: string) => {
    setState(prev => ({
      ...prev,
      isAIGenerating: isGenerating,
      currentAIFile: currentFile
    }));
  }, []);

  // Track user editing status
  const updateUserEditingStatus = useCallback((filePath?: string) => {
    setState(prev => ({
      ...prev,
      userEditingFile: filePath
    }));
  }, []);

  // Mark content as AI-generated
  const markAIContent = useCallback((filePath: string, content: string, startLine = 0, endLine = -1) => {
    setState(prev => {
      const newOrigins = new Map(prev.fileOrigins);
      const existing = newOrigins.get(filePath);
      
      const section: ContentSection = {
        id: `ai-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        startLine,
        endLine: endLine === -1 ? content.split('\n').length - 1 : endLine,
        origin: 'ai',
        content,
        timestamp: Date.now()
      };

      if (existing) {
        newOrigins.set(filePath, {
          ...existing,
          sections: [...existing.sections, section],
          lastModified: Date.now()
        });
      } else {
        newOrigins.set(filePath, {
          filePath,
          sections: [section],
          lastModified: Date.now()
        });
      }

      return {
        ...prev,
        fileOrigins: newOrigins
      };
    });
  }, []);

  // Mark content as user-edited
  const markUserContent = useCallback((filePath: string, content: string, startLine = 0, endLine = -1) => {
    setState(prev => {
      const newOrigins = new Map(prev.fileOrigins);
      const existing = newOrigins.get(filePath);
      
      const section: ContentSection = {
        id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        startLine,
        endLine: endLine === -1 ? content.split('\n').length - 1 : endLine,
        origin: 'user',
        content,
        timestamp: Date.now()
      };

      if (existing) {
        newOrigins.set(filePath, {
          ...existing,
          sections: [...existing.sections, section],
          lastModified: Date.now()
        });
      } else {
        newOrigins.set(filePath, {
          filePath,
          sections: [section],
          lastModified: Date.now()
        });
      }

      return {
        ...prev,
        fileOrigins: newOrigins
      };
    });
  }, []);

  // Add user edit to queue
  const queueUserEdit = useCallback((filePath: string, content: string) => {
    // If AI is currently editing this file, queue the edit
    if (state.isAIGenerating && state.currentAIFile === filePath) {
      const edit: PendingEdit = {
        id: `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        filePath,
        content,
        timestamp: Date.now(),
        applied: false,
        conflicted: false
      };

      setState(prev => ({
        ...prev,
        pendingEdits: [...prev.pendingEdits, edit]
      }));

      return edit.id;
    }

    // Otherwise, apply immediately and mark as user content
    markUserContent(filePath, content);
    return null;
  }, [state.isAIGenerating, state.currentAIFile, markUserContent]);

  // Detect conflicts between AI and user edits
  const detectConflict = useCallback((filePath: string, aiContent: string, userContent: string) => {
    // Simple conflict detection - if both AI and user have edited the same file recently
    const fileOrigin = state.fileOrigins.get(filePath);
    if (!fileOrigin) return null;

    const recentAIEdit = fileOrigin.sections
      .filter(s => s.origin === 'ai')
      .sort((a, b) => b.timestamp - a.timestamp)[0];

    const recentUserEdit = fileOrigin.sections
      .filter(s => s.origin === 'user')
      .sort((a, b) => b.timestamp - a.timestamp)[0];

    // If both exist and are recent (within 5 minutes of each other)
    if (recentAIEdit && recentUserEdit && 
        Math.abs(recentAIEdit.timestamp - recentUserEdit.timestamp) < 5 * 60 * 1000) {
      
      const conflict: EditConflict = {
        id: `conflict-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        filePath,
        userContent,
        aiContent,
        timestamp: Date.now(),
        resolved: false
      };

      setState(prev => ({
        ...prev,
        conflicts: [...prev.conflicts, conflict]
      }));

      return conflict.id;
    }

    return null;
  }, [state.fileOrigins]);

  // Resolve a conflict
  const resolveConflict = useCallback((conflictId: string, resolution: 'user' | 'ai' | 'merge') => {
    setState(prev => ({
      ...prev,
      conflicts: prev.conflicts.map(conflict =>
        conflict.id === conflictId
          ? { ...conflict, resolved: true, resolution }
          : conflict
      )
    }));
  }, []);

  // Apply all queued edits
  const applyQueuedEdits = useCallback(() => {
    setState(prev => {
      const newState = { ...prev };
      
      // Apply all pending edits
      prev.pendingEdits.forEach(edit => {
        if (!edit.applied) {
          markUserContent(edit.filePath, edit.content);
        }
      });

      // Mark all edits as applied
      newState.pendingEdits = prev.pendingEdits.map(edit => ({
        ...edit,
        applied: true
      }));

      return newState;
    });
  }, [markUserContent]);

  // Get content origin for a file
  const getFileOrigin = useCallback((filePath: string): 'ai' | 'user' | 'mixed' | 'unknown' => {
    const fileOrigin = state.fileOrigins.get(filePath);
    if (!fileOrigin || fileOrigin.sections.length === 0) return 'unknown';

    const hasAI = fileOrigin.sections.some(s => s.origin === 'ai');
    const hasUser = fileOrigin.sections.some(s => s.origin === 'user');

    if (hasAI && hasUser) return 'mixed';
    if (hasAI) return 'ai';
    if (hasUser) return 'user';
    return 'unknown';
  }, [state.fileOrigins]);

  // Check if user can edit a file (not currently being edited by AI)
  const canUserEdit = useCallback((filePath: string): boolean => {
    return !state.isAIGenerating || state.currentAIFile !== filePath;
  }, [state.isAIGenerating, state.currentAIFile]);

  // Get pending edits count for a file
  const getPendingEditsCount = useCallback((filePath?: string): number => {
    if (filePath) {
      return state.pendingEdits.filter(edit => !edit.applied && edit.filePath === filePath).length;
    }
    return state.pendingEdits.filter(edit => !edit.applied).length;
  }, [state.pendingEdits]);

  // Get unresolved conflicts count
  const getUnresolvedConflictsCount = useCallback((): number => {
    return state.conflicts.filter(conflict => !conflict.resolved).length;
  }, [state.conflicts]);

  // Cleanup resolved conflicts and applied edits
  const cleanup = useCallback(() => {
    setState(prev => ({
      ...prev,
      pendingEdits: prev.pendingEdits.filter(edit => !edit.applied),
      conflicts: prev.conflicts.filter(conflict => !conflict.resolved)
    }));
  }, []);

  // Auto-cleanup every 5 minutes
  useEffect(() => {
    const interval = setInterval(cleanup, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [cleanup]);

  return {
    state,
    updateAIStatus,
    updateUserEditingStatus,
    markAIContent,
    markUserContent,
    queueUserEdit,
    detectConflict,
    resolveConflict,
    applyQueuedEdits,
    getFileOrigin,
    canUserEdit,
    getPendingEditsCount,
    getUnresolvedConflictsCount,
    cleanup
  };
}
